import React from "react";
// note to check and remove white space added before the string like name of api and org name
// Load replaceAll shim as its not yet supported on IE
import replaceAll from "string.prototype.replaceall";
import {
  Card,
  PrimaryButton,
  TextButton,
  withTooltip,
  THEMES,
  IconSales,
  TOOLTIP_HORIZONTAL_ALIGNMENTS,
  TOOLTIP_VERTICAL_ALIGNMENTS,
  Divider,
} from "cdk-radial";
import {
  API_DIRECTORY_THEME,
  API_VISIBILITY,
  CONTRACT_TEXT,
  RESOURCE_TYPE,
} from "../../../../utils/constants";
import {
  getPricingText,
  PLAN_STATUSES,
} from "../../../../utils/api-pricing";
import { PLAN, RESTRICTED_SHARED_STATUS } from "../../constants";

import { PrivateIcon } from "../../../common/icons/icons";

const BTN_TEXT = {
  ADD_TO_CART: "Add to Cart",
  ADDED_TO_CART: "Added to Cart",
  VIEW_IN_MY_APIS: "View in My APIs",
  REQUESTED_ACCESS: "Requested Info",
  REQUEST_ACCESS: "Request Info",
};

// Quick and dirty fix to supress markdown content(ex: '#') from displaying on the API cards in directory home page
function suppressMarkdown(input = "") {
  return replaceAll(input, "#", "");
}

function approvedTags(categories) {
  if (categories.length > 1) {
    let newCat = [...categories];
    const compwithToolTip = (props) => {
      return (
        <div {...props}>{`Categories: ${categories[0]} (+${categories.length -
          1})`}</div>
      );
    };

    const Comp = withTooltip(compwithToolTip, {
      id: `tooltip-1`,
      text: getRemainingTags(newCat),
      theme: THEMES.LIGHT,
      className: "api-dir-pkg_tooltip",
    });
    return <Comp />;
  } else if (categories.length === 1) {
    return <span>{`Categories: ${categories[0]}`}</span>;
  } else {
    return <span>Categories:</span>;
  }
}
function getRemainingTags(categories) {
  categories.shift();
  let tags = "";
  categories.map((t) => {
    tags = tags + t + ", ";
  });
  return tags.slice(0, -2);
}

const getTooltipConfig = (tooltipId, text) => ({
  id: tooltipId,
  text,
  theme: THEMES.LIGHT,
});

function descriptionWithTooltip(name) {
  const compwithToolTip = props => {
    return <label className="api-card__publisher" {...props}>{`By ${name}`}</label>
  };
  const APIDescriptionWithTooltip = withTooltip(compwithToolTip, getTooltipConfig('tooltip-id-api-description', name));
  return <APIDescriptionWithTooltip />;
}

const ContractIcon = () => {
  const compwithToolTip = (props) => {
    return <IconSales {...props} className="api-dir-pkg_icons" />;
  };

  const Component = withTooltip(compwithToolTip, {
    id: `contract-icon`,
    text: CONTRACT_TEXT,
    theme: THEMES.LIGHT,
  });
  return (
    <Component
      horizontalAlignment={TOOLTIP_HORIZONTAL_ALIGNMENTS.RIGHT}
      verticalAlignment={TOOLTIP_VERTICAL_ALIGNMENTS.TOP}
    />
  );
};

export const getApiIdsFromBundlesInCart = (cartDetails, apiBundles) => {
  if (!cartDetails || !Array.isArray(cartDetails) || !apiBundles || !Array.isArray(apiBundles)) {
    return [];
  }

  const bundleIdsInCart = cartDetails
    .filter((item) => item.resourceType === RESOURCE_TYPE.BUNDLE)
    .map((item) => item.resourceId);

  const apiIdsFromBundlesInCart = apiBundles
    .filter((bundle) => bundleIdsInCart.includes(bundle.id))
    .flatMap((bundle) => bundle.apis?.map((api) => api.id) || []);

  return apiIdsFromBundlesInCart;
};


const ApiCard = (props) => {
  const {
    api: {
      id,
      name,
      description,
      visibility,
      kind,
      tags,
      summary,
      orgName,
      orgLogo,
      isContracted,
      plans,
      isRestricted,
      restrictedSharedStatus,
      skipApiPricing,
      orgId
    },
    domainsToDisplay,
    entity,
    cartDetails,
    apiHomeOnClickHandlers,
    belongsTo,
    orderDetails,
    requestStatus,
    isMsaConsented,
    requestingOrgId,
    apiBundles
  } = props;

  const ownOrgApi = requestingOrgId === orgId;
  const shouldSkipRestriction = isContracted || ownOrgApi || skipApiPricing;

  const isEligibleForRestriction =
    isRestricted &&
    !shouldSkipRestriction &&
    [
      RESTRICTED_SHARED_STATUS.NOT_REQUESTED,
      RESTRICTED_SHARED_STATUS.REQUESTED,
    ].includes(restrictedSharedStatus);


  const categories =
    domainsToDisplay &&
    domainsToDisplay.length &&
    domainsToDisplay
      .filter((approvedTag) => tags.includes(approvedTag.id))
      .map((tag) => tag.name);
  const getSummary = () => {
    if (!!summary) {
      return suppressMarkdown(summary);
    } else {
      return suppressMarkdown(description);
    }
  };

  const isAPIInCart =
    cartDetails && Array.isArray(cartDetails) && cartDetails?.some((api) => api.resourceId === id);

  const apiIdsFromBundlesInCart = getApiIdsFromBundlesInCart(cartDetails, apiBundles);
  const isCurrentApiInBundleCart = apiIdsFromBundlesInCart.includes(id);

  const allOrderedAPIs = [...(orderDetails?.restApis || []), ...(orderDetails?.asyncApis || [])];
  const isAPIInOrder =
     allOrderedAPIs && allOrderedAPIs?.some?.((api) => api.id === id);
  const showContractIcon = isContracted;

  const requestProcessing = requestStatus && requestStatus[id]?.isLoading || false;

  const handleRequestAccess = (event) => {
    if (event.target.innerText === BTN_TEXT.REQUEST_ACCESS) {
      apiHomeOnClickHandlers.requestRestrictedAccess({
        apiId: id,
        apiType: kind,
        btn: BTN_TEXT.REQUEST_ACCESS,
      });
    }

    event.stopPropagation();
  };

  const handleAddToCart = (event) => {
    if (event.target.innerText === BTN_TEXT.ADD_TO_CART)
      apiHomeOnClickHandlers.addApiToCart({
        resourceId: id,
        resourceType: kind,
        btn: BTN_TEXT.ADD_TO_CART,
      });
    event.stopPropagation();
  };

  const handleSeePricingButton = (event) => {
    event.stopPropagation();

    apiHomeOnClickHandlers.viewApiPricingTab({
      apiId: id,
      apiType: kind,
    });
  };

  const handleViewInMyAPIs = (event) => {
    event.stopPropagation();
    if (event.target.innerText === BTN_TEXT.VIEW_IN_MY_APIS)
      apiHomeOnClickHandlers.viewInMyAPIs({
        apiId: id,
        apiType: kind,
        btn: BTN_TEXT.VIEW_IN_MY_APIS,
      });
  };

  const activePricingPlans =
    plans?.length && plans.filter((f) => f?.status === PLAN_STATUSES.ACTIVE);

  const dealerPlanArray =
    Array.isArray(activePricingPlans) &&
    activePricingPlans?.length &&
    activePricingPlans.filter((f) => f?.dealerPriceChecked);

  const pricingInfo = getPricingText(activePricingPlans);

  const ISVCard = () => (
    <Card
        dataTestId="api-card-test-id"
        data-cy={`spec-card__${name}`}
        className="api-card"
      >
        <div className="api-card__container">
          <div className="api-dir-pkg_api-category-misc_isv">
            {visibility === API_VISIBILITY.PRIVATE ? (
              <PrivateIcon />
            ) : null}
            {showContractIcon && <ContractIcon className="api-dir-pkg_icons" />}
          </div>
          <div className="api-card___name_description">
            {nameWithTooltip(name)}
            {descriptionWithTooltip(orgName)}
          </div>
          <p className="api-dir-pkg_api_card_description_isv">{getSummary()}</p>
          <Divider className="api-dir-pkg_category-divider" />
          <span className="api-dir-pkg_api_category_tags">
            {approvedTags(categories)}
          </span>
        </div>
    </Card>
  );

  const renderDealerCardCTA = () => {
    if (isEligibleForRestriction) {
      const restrictedApiNotRequested =
        restrictedSharedStatus === RESTRICTED_SHARED_STATUS.NOT_REQUESTED;

      return (
       <div className="api-dir-pkg-add-to-cart">
         <PrimaryButton
          text={
             restrictedApiNotRequested
               ? BTN_TEXT.REQUEST_ACCESS
               : BTN_TEXT.REQUESTED_ACCESS
          }
          className={
            restrictedApiNotRequested
              ? "api-dir-pkg_request_access_btn"
              : "api-dir-pkg_access_requested_btn"
          }
          isDisabled={!restrictedApiNotRequested}
           isLoading={requestProcessing}
          onClick={(event) => handleRequestAccess(event)}
        />
       </div>
      );
    }

    if (isAPIInOrder) {
      return (
        <div className="api-dir-pkg_view-apis-button">
          <TextButton
            text={BTN_TEXT.VIEW_IN_MY_APIS}
            className="api-dir-pkg_view_in_my_apis_btn"
            onClick={(event) => handleViewInMyAPIs(event)}
          />
        </div>
      );
    }

    if (isAPIInCart || isCurrentApiInBundleCart) {
      return (
        <TextButton
          isDisabled
          text={BTN_TEXT.ADDED_TO_CART}
          className="api-dir-pkg_added_to_cart_btn"
        />
      );
    }
  
    if (!isContracted) {
      return (
        <div className="api-dir-pkg-add-to-cart">
          <PrimaryButton
            text={BTN_TEXT.ADD_TO_CART}
            onClick={handleAddToCart}
            className={
              activePricingPlans?.length
                ? "api-dir-pkg_add_to_cart_btn_with_pricing"
                : "api-dir-pkg_add_to_cart_btn"
            }
            isLoading={requestProcessing}
            isDisabled={!isMsaConsented}
          />
        </div>
      );
    }
  
    return null;
  };

  const DealerCard = () => (
    <Card
      dataTestId="api-card-test-id"
      data-cy={`spec-card__${name}`}
      className="api-dir-pkg_spec-card"
      onClick={(event) => {
        if (belongsTo !== API_DIRECTORY_THEME.ISV_API_DIRECTORY) {
          event.stopPropagation();
          apiHomeOnClickHandlers.handleApiRedirection({
            apiId: id,
            apiType: kind,
          });
        }
      }}
    >
      <div className="api-dir-pkg_top_div">
        <div className="api-dir-pkg_api-category-misc_dealer">
          {visibility === API_VISIBILITY.PRIVATE ? (
            <PrivateIcon className="api-dir-pkg_icons" />
          ) : null}
          {showContractIcon && <ContractIcon className="api-dir-pkg_icons" />}
        </div>
        <div className="api-dir-pkg_api-card-description">
            {nameWithTooltip(name)}
            {descriptionWithTooltip(orgName)}
          </div>
        <div className="api-dir-pkg_api_card_description_dealer_directory">
          {getSummary()}
        </div>
        {isEligibleForRestriction ? (
          <>
            <div className="api-dir-pkg_pricing-plan-helper-section">
              <span className="api-dir-pkg_pricing-plan-helper-text">
                Request only API
              </span>
              <Divider className="api-dir-pkg_see-pricing-divider"></Divider>
            </div>
          </>
        ) : null}
        {activePricingPlans?.length &&
          !isAPIInOrder &&
        !showContractIcon &&
        !isEligibleForRestriction ? (
            <>
              <div className="api-dir-pkg_pricing-plan-helper-section">
                <span className="api-dir-pkg_pricing-plan-helper-text">
                  Starting from
                </span>
                {dealerPlanArray?.length > 1 ? (
                  <span
                    className="api-dir-pkg_pricing-plan-button"
                    onClick={handleSeePricingButton}
                  >
                    See all Pricing Plans ({dealerPlanArray?.length})
                  </span>
              ) : !dealerPlanArray?.length && activePricingPlans?.length > 1 ? (
                  <span
                    className="api-dir-pkg_pricing-plan-button"
                    onClick={handleSeePricingButton}
                  >
                    See all Pricing Plans ({activePricingPlans?.length})
                  </span>
                ) : null}
                <Divider className="api-dir-pkg_see-pricing-divider"></Divider>
              </div>
            </>
        ) : null}
        {activePricingPlans?.length &&
          !isAPIInOrder &&
        !showContractIcon &&
        !isEligibleForRestriction ? (
            <div className="api-dir-pkg_pricing-text-section">
              <span className="api-dir-pkg_pricing-dollar-span">$</span>
              {` `}
              <span className="api-dir-pkg_pricing-amount-span">
                {pricingInfo?.lowestPricingAmount}
              </span>
              {` `}
              <div className="api-dir-pkg_pricing-info">
                <span className="api-dir-pkg_pricing-info-month-span">
                  {pricingInfo?.lowestPricingPlanName === PLAN.SUBSCRIPTION || pricingInfo?.isDealerSubscriptionPlan ? 
                    "/location per month" :  
                    "/month"} 
                  {" "}
                </span>
                <span className="api-dir-pkg_pricing-info-plan-name">
                  {" "}
                  {pricingInfo?.lowestPricingPlanName}
                </span>
              </div>
            </div>
        ) : null}
        {renderDealerCardCTA()}
      </div>
    </Card>
  );

  return !name && (!summary || !description) 
    ? null 
    : belongsTo === API_DIRECTORY_THEME.ISV_API_DIRECTORY 
      ? <ISVCard /> 
      : <DealerCard />;
};

export const nameWithTooltip = (name = "") => {
  const apiName = name?.trim();
  const compwithToolTip = props => {
    return <h4 className="api-card__api_name" {...props}>{apiName}</h4>
  };
  const APINameWithTooltip = withTooltip(compwithToolTip, getTooltipConfig('tooltip-id-api-name', `API Name: ${apiName}`));
  return <APINameWithTooltip />;
}

export default ApiCard;
